import React, { useEffect, useState } from 'react';
import { translate } from '../../services/translate';
import { Modal, View } from 'react-native';
import FormActions from '../FormActions';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../CustomButton';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { UnitAndDistance } from './OccurrenceDetailsDispatchTab';
import DisplacedReasonDropdown from '../dispatch/DisplacedReasonDropdown';
import PoliceStationDropdown from '../dispatch/PoliceStationDropdown';

interface Props {
    displaceReasonId: number;
    setDisplaceReasonId: React.Dispatch<React.SetStateAction<number>>;
    policeStationId: number;
    setPoliceStationId: React.Dispatch<React.SetStateAction<number>>;
    isModalVisible: boolean;
    setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
    onSubmit: () => void;
    onRemoveDisplaced: () => void;
    isDisplaced: boolean;
    unit: UnitAndDistance;
}

export default function DisplacedModal({
    isModalVisible,
    setIsModalVisible,
    displaceReasonId,
    setDisplaceReasonId,
    onSubmit,
    onRemoveDisplaced,
    isDisplaced,
    unit,
    policeStationId,
    setPoliceStationId,
}: Props) {
    const { styles } = useStyles(styleSheet);

    const [category, setCategory] = useState<'arrested' | 'hospitalized' | null | undefined>();

    const [initialDisplaceReasonId, setInitialDisplaceReasonId] = useState(displaceReasonId);
    const [initialPoliceStationId, setInitialPoliceStationId] = useState(policeStationId);
    const [submitted, setSubmitted] = useState(false);


    useEffect(() => {
        if (isModalVisible) {
            setInitialDisplaceReasonId(displaceReasonId);
            setInitialPoliceStationId(policeStationId);
            setSubmitted(false);

            if (displaceReasonId === 0) {
                setCategory(undefined);
            }
        }
    }, [isModalVisible]);

    useEffect(() => {
        if (category === undefined) {
            return;
        }
        if (category !== 'arrested') {
            setPoliceStationId(0);
        }
    }, [category]);


    function handleSubmit() {
        setSubmitted(true);
        onSubmit();
    }

    function onCancel() {
        if (submitted) {
            setSubmitted(false);
            setIsModalVisible(false);
            return;
        }

        if (displaceReasonId !== initialDisplaceReasonId) {
            setDisplaceReasonId(initialDisplaceReasonId);
        }
        if (policeStationId !== initialPoliceStationId) {
            setPoliceStationId(initialPoliceStationId);
        }

        setCategory(undefined);
        setIsModalVisible(false);
    }

    return (
        <Modal transparent={true} visible={isModalVisible} animationType='fade' onRequestClose={onCancel}>
            <View style={styles.container}>
                <View style={styles.formContainer}>
                    <DisplacedReasonDropdown
                        setValue={(value, item) => {
                            setDisplaceReasonId(value);
                            setCategory(item?.category);
                        }}
                        value={displaceReasonId}
                        unitName={unit.unitName.name}
                        zIndex={3}
                    />
                    {(category === 'arrested' || policeStationId) ? (
                        <PoliceStationDropdown
                            setValue={setPoliceStationId}
                            value={policeStationId}
                            zIndex={2}
                        />
                    ) : null}
                    {isDisplaced ? (
                        <CustomButton
                            onPress={() => onRemoveDisplaced()}
                            text={translate('removeDisplaced')}
                            icon={faTimes}
                        />
                    ) : null}
                    <FormActions
                        onSubmit={async () => handleSubmit()}
                        onClose={onCancel}
                        disabled={displaceReasonId == 0}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        gap: 20,
    },
}));
